import { getJSON } from 'common/services/api'
import { useMemo } from 'react'
import { useQuery } from 'react-query'

// Two endpoints, mirroring the needs pattern (`/v6/needs/{options,suggestions}`):
//   options     = distinct job titles already used in the current home
//                 (server returns them alphabetical + case-insensitively deduped)
//   suggestions = curated UK Adult Social Care baseline, in published order
// Both are wrapped as { count, data: [{ name }] } by the backend (wrapDataList).
const OPTIONS_URL = '/v6/job-titles/options'
const SUGGESTIONS_URL = '/v6/job-titles/suggestions'

// Shared so callers that add a job title (e.g. creating a team member) can
// invalidate both queries: `queryClient.invalidateQueries([JOB_TITLES_QUERY_KEY])`.
export const JOB_TITLES_QUERY_KEY = 'job-titles'

type JobTitleItem = { name: string }

const fetchNames = async (url: string): Promise<string[]> => {
  try {
    // `no-store`: these GETs carry an ETag, and a 304 revalidation breaks getJSON
    // (304 isn't `response.ok`). React Query caches the result in memory anyway.
    const result = (await getJSON({ url, cache: 'no-store' })) as {
      data?: JobTitleItem[]
    }
    return (result.data ?? []).map((item) => item.name)
  } catch {
    return []
  }
}

type UseJobTitlesProps = {
  enabled?: boolean
}

type UseJobTitlesReturn = {
  jobTitles: string[]
  isLoading: boolean
}

const useJobTitles = ({
  enabled = true
}: UseJobTitlesProps = {}): UseJobTitlesReturn => {
  const { data: options = [], isLoading: optionsLoading } = useQuery<string[]>(
    [JOB_TITLES_QUERY_KEY, 'options'],
    () => fetchNames(OPTIONS_URL),
    { enabled, retry: false }
  )
  const { data: suggestions = [], isLoading: suggestionsLoading } = useQuery<
    string[]
  >([JOB_TITLES_QUERY_KEY, 'suggestions'], () => fetchNames(SUGGESTIONS_URL), {
    enabled,
    retry: false
  })

  // Home titles first (server already alphabetical), then curated baseline
  // entries not already present — deduped case-insensitively.
  const jobTitles = useMemo(() => {
    const seen = new Set(options.map((title) => title.toLowerCase()))
    const merged = [...options]
    for (const title of suggestions) {
      const key = title.toLowerCase()
      if (!seen.has(key)) {
        seen.add(key)
        merged.push(title)
      }
    }
    return merged
  }, [options, suggestions])

  return { jobTitles, isLoading: optionsLoading || suggestionsLoading }
}

export default useJobTitles
